import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { EditButton, DeleteButton } from '../../components/ActionButtons';
import { apiRequest } from '../../services/api';
import UsageInfo from '../../components/UsageInfo';
import LoadingSpinner from '../../components/LoadingSpinner';
import EmptyState from '../../components/EmptyState';
import PageHeader from '../../components/PageHeader';
import ModalShell from '../../components/ModalShell';

function PermissionGrid({ yetkiTanimlari, seciliIds, onChange }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
      {[0, 1, 2].map(col => (
        <div key={col} className="space-y-3">
          {(yetkiTanimlari || []).filter(y => y.kolon === col).sort((a, b) => a.siraNo - b.siraNo).map(y => {
            const secili = seciliIds.includes(y.yetkiTanimiId);
            return (
              <label key={y.yetkiTanimiId}
                className={`flex items-start gap-4 p-4 rounded-[1.5rem] border-2 transition-[border-color,background-color,box-shadow,transform] duration-300 cursor-pointer group
                  ${secili 
                    ? 'border-indigo-400 bg-indigo-50/50 shadow-lg shadow-indigo-100/50 scale-[1.02]' 
                    : 'border-gray-50 bg-gray-50/30 hover:border-gray-200 hover:bg-gray-100/50'}`}>
                <div
                  className={`w-5 h-5 mt-0.5 rounded-lg flex-shrink-0 border-2 flex items-center justify-center transition-[background-color,border-color,box-shadow] duration-300
                    ${secili ? 'bg-indigo-600 border-indigo-600 shadow-lg shadow-indigo-200' : 'border-gray-300 bg-white group-hover:border-indigo-300'}`}
                  onClick={(e) => { e.preventDefault(); onChange(y.yetkiTanimiId, !secili); }}>
                  {secili && <span className="text-white text-xs font-black">✓</span>}
                </div>
                <div className="min-w-0">
                  <div className="text-xs font-black text-gray-800 leading-snug flex flex-wrap items-center gap-2 uppercase tracking-tight">
                    {y.etiket}
                    {y.rozet && <span className="bg-emerald-500 text-white text-[8px] font-black px-2 py-0.5 rounded-full leading-none shadow-sm">{y.rozet}</span>}
                  </div>
                  {y.aciklama && <div className="text-[10px] font-bold text-gray-400 leading-tight mt-1.5 opacity-80">{y.aciklama}</div>}
                  <div className="mt-2 flex items-center">
                    <span className="font-mono text-[9px] text-indigo-400 bg-indigo-50 px-1.5 py-0.5 rounded-md font-bold uppercase border border-indigo-100/50 opacity-60 group-hover:opacity-100 transition-opacity">
                      {y.anahtar}
                    </span>
                  </div>
                </div>
              </label>
            );
          })}
        </div>
      ))}
    </div>
  );
}

function RolFormModal({ rol, yetkiTanimlari, onKaydet, onKapat, kaydediliyor, hata }) {
  const yeni = !rol;
  const [form, setForm] = useState({
    ad: rol?.ad ?? '',
    aciklama: rol?.aciklama ?? '',
    aktifMi: rol?.aktifMi ?? true,
    uyariAktif: rol?.uyariAktif ?? false,
    uyariAraligi: rol?.uyariAraligi ?? 60,
    uyariMesaj: rol?.uyariMesaj ?? '',
    yetkiTanimiIdleri: rol?.yetkiTanimiIdleri ?? [],
  });

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const toggleYetki = (id, secili) => {
    setForm(p => ({
      ...p,
      yetkiTanimiIdleri: secili
        ? [...p.yetkiTanimiIdleri, id]
        : p.yetkiTanimiIdleri.filter(x => x !== id),
    }));
  };

  const hepsiSecili = yetkiTanimlari.length > 0 && yetkiTanimlari.every(y => form.yetkiTanimiIdleri.includes(y.yetkiTanimiId));

  const INP = "w-full border-2 border-gray-100 rounded-2xl px-5 py-3.5 text-sm font-bold text-gray-800 focus:outline-none focus:border-indigo-400 focus:ring-4 focus:ring-indigo-50 bg-white transition-all placeholder-gray-300";

  return (
    <ModalShell
      icon={yeni ? '➕' : '✏️'}
      iconColor="from-indigo-600 to-violet-700"
      title={yeni ? 'Yeni Rol Tanımla' : `"${rol.ad}" Düzenle`}
      subtitle="Erişim yetkileri ve sistem hatırlatıcıları"
      onClose={onKapat}
      size="xl"
      zIndex="z-[70]"
      footer={
        <>
          <button onClick={onKapat} className="px-8 py-3.5 rounded-2xl border-2 border-gray-100 text-xs font-black text-gray-500 hover:bg-gray-100 hover:border-gray-200 transition-all uppercase tracking-widest">İPTAL</button>
          <button
            onClick={() => onKaydet({ ...(rol ? { rolId: rol.rolId } : {}), ...form })}
            disabled={kaydediliyor}
            className="px-12 py-3.5 bg-gradient-to-r from-indigo-600 to-violet-700 hover:from-indigo-700 hover:to-violet-800 disabled:opacity-60 text-white rounded-2xl text-xs font-black shadow-xl shadow-indigo-100 transition-all hover:-translate-y-1 active:translate-y-0 uppercase tracking-widest">
            {kaydediliyor ? '⌛...' : '💾 ROLÜ KAYDET'}
          </button>
        </>
      }
    >
      {hata && <div className="bg-rose-50 border-2 border-rose-100 rounded-2xl px-6 py-4 text-xs text-rose-600 font-black flex items-center gap-3 animate-shake"><span>⚠️</span> {hata}</div>}

      {/* Temel Bilgiler */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-3 ml-1">ROL ADI / ETİKET *</label>
          <input value={form.ad} onChange={e => {
            const v = e.target.value;
            const formatted = v.toLocaleLowerCase('tr-TR').split(' ').map(s => s.charAt(0).toLocaleUpperCase('tr-TR') + s.slice(1)).join(' ');
            set('ad', formatted);
          }} className={INP} placeholder="Örn: Ön Büro, Yönetici..." />
        </div>
        <div>
          <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-3 ml-1">KISA AÇIKLAMA</label>
          <input value={form.aciklama} onChange={e => set('aciklama', e.target.value)} className={INP} placeholder="Bu rolün görev tanımı..." />
        </div>
      </div>

      {/* Hatırlatıcı Bloğu */}
      <div className="bg-amber-50/50 border-2 border-amber-100 rounded-[2rem] p-6 md:p-8 space-y-5 relative overflow-hidden group">
        <div className="absolute top-0 right-0 -mr-10 -mt-10 w-40 h-40 bg-amber-200 rounded-full blur-3xl opacity-20 group-hover:opacity-40 transition-opacity" style={{ transform: 'translateZ(0)', willChange: 'transform' }}></div>
        <div className="relative flex items-start sm:items-center justify-between gap-6">
          <div className="min-w-0">
            <p className="text-base font-black text-amber-800 tracking-tight flex items-center gap-2">
              <span className="text-xl">⏰</span> GÖREV HATIRLATICI (POPUP)
            </p>
            <p className="text-xs font-bold text-amber-600/80 mt-1 max-w-md">Personele periyodik olarak "Aktif çalışıyor musunuz?" uyarısı gönderir ve dijital imza/onay alır.</p>
          </div>
          <button type="button"
            onClick={() => set('uyariAktif', !form.uyariAktif)}
            className={`flex-shrink-0 relative inline-flex h-8 w-14 items-center rounded-full transition-[background-color,box-shadow] duration-300 shadow-lg ${form.uyariAktif ? 'bg-amber-500 shadow-amber-200' : 'bg-gray-300 shadow-gray-100'}`}>
            <span className={`inline-block h-6 w-6 transform rounded-full bg-white shadow-xl transition-transform duration-300 ${form.uyariAktif ? 'translate-x-7' : 'translate-x-1'}`} />
          </button>
        </div>

        {form.uyariAktif && (
          <div className="pt-4 space-y-5 border-t border-amber-200/50 relative z-10 animate-fade-in">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <label className="text-[10px] font-black text-amber-700 uppercase tracking-widest min-w-[120px]">Uyarı Aralığı</label>
              <div className="flex items-center gap-3">
                <input
                  type="number" min="5" max="480"
                  value={form.uyariAraligi}
                  onChange={e => set('uyariAraligi', parseInt(e.target.value) || 60)}
                  className="w-24 border-2 border-amber-200 rounded-2xl px-4 py-2.5 text-sm font-black text-amber-800 focus:outline-none focus:border-amber-500 bg-white text-center shadow-inner"
                />
                <div className="flex flex-col">
                  <span className="text-[10px] font-black text-amber-600 uppercase tracking-widest leading-none">DAKİKA</span>
                  <span className="text-[9px] font-bold text-amber-400 uppercase mt-0.5">
                    ({form.uyariAraligi >= 60 ? `${Math.floor(form.uyariAraligi / 60)} Saat ${form.uyariAraligi % 60} Dk` : `${form.uyariAraligi} Dakika`})
                  </span>
                </div>
              </div>
            </div>
            <div>
              <label className="text-[10px] font-black text-amber-700 uppercase tracking-widest block mb-3 ml-1">Özel Popup Mesajı</label>
              <input
                type="text"
                value={form.uyariMesaj}
                onChange={e => set('uyariMesaj', e.target.value)}
                placeholder='Örn: "Güvenlik turu periyodu geldi, lütfen onaylayın."'
                className="w-full border-2 border-amber-200 rounded-2xl px-5 py-3.5 text-sm font-bold text-amber-800 focus:outline-none focus:border-amber-500 bg-white placeholder-amber-300 shadow-inner"
              />
            </div>
          </div>
        )}
      </div>

      {/* Yetki Seçimi */}
      <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-100 pb-4">
          <div className="flex items-center gap-3">
            <span className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">YETKİ ATAMALARI</span>
            <span className="bg-indigo-600 text-white text-[10px] font-black px-3 py-1 rounded-full shadow-lg shadow-indigo-100">
              {form.yetkiTanimiIdleri.length} / {yetkiTanimlari.length} SEÇİLİ
            </span>
          </div>
          <button
            onClick={() => set('yetkiTanimiIdleri', hepsiSecili ? [] : yetkiTanimlari.map(y => y.yetkiTanimiId))}
            className="text-[10px] font-black text-indigo-600 hover:text-white bg-indigo-50 hover:bg-indigo-600 px-5 py-2.5 rounded-xl transition-all border border-indigo-100 flex items-center gap-2 uppercase tracking-widest shadow-sm">
            {hepsiSecili ? '⬜ TÜMÜNÜ KALDIR' : '☑️ TÜMÜNÜ SEÇ'}
          </button>
        </div>

        {yetkiTanimlari.length === 0 ? (
          <div className="py-12 bg-gray-50 rounded-[2rem] text-center border-4 border-dashed border-gray-100">
            <p className="text-xl mb-2 opacity-30">🔐</p>
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Henüz yetki tanımı bulunmuyor.</p>
          </div>
        ) : (
          <PermissionGrid yetkiTanimlari={yetkiTanimlari} seciliIds={form.yetkiTanimiIdleri} onChange={toggleYetki} />
        )}
      </div>
    </ModalShell>
  );
}

export default function Roller() {
  const [roller, setRoller] = useState([]);
  const [yetkiTanimlari, setYetkiTanimlari] = useState([]);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [modal, setModal] = useState(null);
  const [silOnay, setSilOnay] = useState(null);
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const [modalHata, setModalHata] = useState(null);
  const isOpen = modal === 'yeni' || (modal && modal.rolId);

  const veriYukle = useCallback(async () => {
    setYukleniyor(true);
    try {
      const [r, y] = await Promise.all([
        apiRequest('roller'),
        apiRequest('yetkitanimlari/aktif'),
      ]);
      setRoller(r || []);
      setYetkiTanimlari(y || []);
    } catch { /* sessiz hata */ }
    setYukleniyor(false);
  }, []);

  useEffect(() => { veriYukle(); }, [veriYukle]);

  async function handleDuzenleClick(rol) {
    setModalHata('');
    try {
      const detay = await apiRequest(`roller/${rol.rolId}`);
      setModal(detay);
    } catch {
      setModal(rol);
    }
  }

  async function kaydet(form) {
    setKaydediliyor(true); setModalHata('');
    try {
      if (form.rolId) await apiRequest('roller', 'PUT', form);
      else await apiRequest('roller', 'POST', form);
      await veriYukle();
      setModal(null);
    } catch (e) {
      setModalHata(e?.message || 'Kayıt sırasında hata oluştu.');
    }
    setKaydediliyor(false);
  }

  async function handleSil(id) {
    try {
      await apiRequest(`roller/${id}`, 'DELETE');
      setRoller(p => p.filter(x => x.rolId !== id));
      setSilOnay(null);
    } catch { alert('Silme işlemi başarısız.'); }
  }

  async function toggleAktif(rol) {
    try {
      await apiRequest(`roller/${rol.rolId}/aktif?aktif=${!rol.aktifMi}`, 'PATCH');
      setRoller(p => p.map(x => x.rolId === rol.rolId ? { ...x, aktifMi: !x.aktifMi } : x));
    } catch { alert('İşlem başarısız.'); }
  }

  return (
    <div className="p-3 md:p-6 space-y-7 min-h-screen bg-gray-50/50 animate-fadeIn" style={{ transform: 'translateZ(0)' }}>
      
      <PageHeader
        icon="🛡️"
        iconColor="from-indigo-600 to-violet-700"
        iconShadow="shadow-indigo-200"
        title="Rol Yönetimi"
        subtitle="Yetki gruplarını tanımlayın ve personele atayın"
      >
        <button onClick={() => { setModalHata(''); setModal('yeni'); }}
          className="group flex-shrink-0 flex items-center justify-center gap-4 bg-gradient-to-r from-indigo-600 to-violet-700 hover:from-indigo-700 hover:to-violet-800 text-white px-10 py-4 rounded-2xl text-sm font-black shadow-2xl shadow-indigo-100 transition-all hover:-translate-y-1 active:translate-y-0 uppercase tracking-widest overflow-hidden relative">
          <span className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300"></span>
          <span className="relative text-xl transition-transform group-hover:rotate-90">＋</span>
          <span className="relative">YENİ ROL EKLE</span>
        </button>
      </PageHeader>

      <div className="bg-white rounded-[2.5rem] border border-gray-200 overflow-hidden shadow-sm">
        <div className="flex items-center justify-between px-8 py-5 border-b border-gray-100 bg-gray-50/30">
          <div className="flex items-center gap-4">
            <span className="text-lg">🔐</span>
            <span className="text-[10px] font-black text-gray-800 uppercase tracking-[0.2em]">Sistemde Kayıtlı Roller</span>
            <span className="bg-indigo-600 text-white text-[10px] font-black px-2.5 py-1 rounded-full shadow-lg shadow-indigo-100 scale-90">{roller.length}</span>
          </div>
        </div>

        {yukleniyor ? (
          <LoadingSpinner fullPage text="Rol listesi güncelleniyor..." color="indigo" />
        ) : roller.length === 0 ? (
          <EmptyState icon="🛡️" text="Henüz bir erişim rolü tanımlanmadı" />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-6 p-6 md:p-10">
            {roller.map(rol => (
              <div key={rol.rolId} className={`group relative bg-white border-2 rounded-[2rem] p-6 hover:border-indigo-200 hover:shadow-2xl hover:shadow-indigo-100/50 transition-all duration-500 overflow-hidden ${!rol.aktifMi ? 'opacity-60 grayscale' : 'border-gray-50 shadow-sm'}`}>
                <div className="absolute top-0 right-0 w-24 h-24 bg-indigo-50 rounded-bl-[4rem] group-hover:bg-indigo-100 transition-all duration-500 -mr-4 -mt-4 opacity-50"></div>
                
                <div className="relative z-10 flex items-center justify-between mb-8">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center text-white text-xl font-black shadow-xl shadow-indigo-100 group-hover:scale-110 group-hover:rotate-3 transition-transform duration-500">
                    {(rol.ad || '?')[0].toUpperCase()}
                  </div>
                  <button onClick={() => toggleAktif(rol)}
                    className={`flex-shrink-0 text-[10px] font-black px-4 py-1.5 rounded-xl border transition-all uppercase tracking-widest shadow-sm
                      ${rol.aktifMi ? 'bg-emerald-50 text-emerald-600 border-emerald-100 hover:bg-emerald-600 hover:text-white' : 'bg-rose-50 text-rose-600 border-rose-100 hover:bg-rose-600 hover:text-white'}`}>
                    {rol.aktifMi ? '● AKTİF' : '● PASİF'}
                  </button>
                </div>

                <div className="relative z-10 min-w-0 mb-8">
                  <h3 className="font-black text-gray-800 text-lg uppercase tracking-tight truncate group-hover:text-indigo-600 transition-colors">{rol.ad}</h3>
                  {rol.aciklama ? (
                    <p className="text-[11px] font-bold text-gray-400 mt-2 line-clamp-2 min-h-[2rem] leading-relaxed uppercase tracking-tight">{rol.aciklama}</p>
                  ) : (
                    <div className="h-[2rem] mt-2 border-b-2 border-dashed border-gray-100 w-1/2"></div>
                  )}
                </div>

                <div className="relative z-10 grid grid-cols-2 gap-4 mb-8">
                  <div className="bg-gray-50/80 rounded-2xl p-4 border border-gray-100 flex flex-col items-center group-hover:bg-indigo-50/50 transition-colors">
                    <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1.5 opacity-60">YETKİLER</span>
                    <div className="flex items-baseline gap-1 text-indigo-600">
                      <span className="text-xl font-black">{rol.yetkiTanimiIdleri?.length ?? 0}</span>
                      <span className="text-[10px] font-bold opacity-30">/{yetkiTanimlari.length}</span>
                    </div>
                  </div>
                  <div className={`bg-gray-50/80 rounded-2xl p-4 border border-gray-100 flex flex-col items-center transition-all ${rol.uyariAktif ? 'border-amber-100 bg-amber-50 group-hover:bg-amber-100/50' : 'opacity-40 group-hover:opacity-100'}`}>
                    <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1.5 opacity-60">POPUP</span>
                    <span className="text-lg">{rol.uyariAktif ? '⏰' : '❌'}</span>
                    {rol.uyariAktif && <span className="text-[9px] font-black text-amber-600 uppercase mt-1">{rol.uyariAraligi} DK</span>}
                  </div>
                </div>

                <div className="relative z-10 flex gap-2 pt-6 border-t border-gray-50">
                  <EditButton size="lg" onClick={() => handleDuzenleClick(rol)} />
                  <DeleteButton size="lg" onClick={() => setSilOnay(rol.rolId)} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <UsageInfo
        color="indigo"
        description="Rol Yönetimi, personellere atanacak yetki setlerini (profilleri) oluşturduğunuz merkezi alandır. Doğrudan personellere yetki vermek yerine, önce rolleri tanımlayıp ardından personelleri bu rollere bağlamanız yönetim kolaylığı sağlar."
        scenarios={[
          { title: "Statü ve Erişim", content: "Bir personele 'Resepsiyon' rolü verdiğinizde, o rolün sahip olduğu tüm yetkiler (Rezervasyon, Oda Durum vb.) otomatik olarak tanımlanır." },
          { title: "Dijital Mesai Kontrolü", content: "Görev Hatırlatıcı özelliği sayesinde, personellere periyodik aralıklarla ekranlarında beliren onay kutucukları göndererek aktif çalışma takibi yapabilirsiniz." },
          { title: "Toplu Güncelleme", content: "Bir rolün yetkilerini değiştirdiğinizde, bu role sahip olan yüzlerce personelin erişim hakları saniyeler içinde otomatik olarak güncellenir." }
        ]}
      />

      {isOpen && createPortal(
        <RolFormModal
          rol={modal === 'yeni' ? null : modal}
          yetkiTanimlari={yetkiTanimlari}
          onKaydet={kaydet}
          onKapat={() => setModal(null)}
          kaydediliyor={kaydediliyor}
          hata={modalHata}
        />,
        document.body
      )}

      {silOnay && createPortal(
        <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center px-4 backdrop-blur-md animate-fade-in shadow-2xl">
          <div className="bg-white rounded-[2.5rem] shadow-2xl p-10 w-full max-w-sm text-center border border-gray-100">
            <div className="w-24 h-24 bg-rose-50 rounded-[2.5rem] flex items-center justify-center mx-auto mb-6 text-5xl shadow-xl shadow-rose-100/50 animate-bounce-subtle">🗑️</div>
            <h3 className="font-black text-gray-900 text-xl tracking-tight uppercase leading-none">Rolü Sil</h3>
            <p className="text-[11px] font-black text-gray-400 mt-5 mb-10 leading-relaxed uppercase tracking-tight"> 
              <b className="text-gray-900">"{roller.find(x => x.rolId === silOnay)?.ad}"</b> rolü kalıcı olarak silinecek. Bu role bağlı personellerin yetkileri sıfırlanabilir. Emin misiniz?
            </p>
            <div className="flex gap-4">
              <button onClick={() => setSilOnay(null)} className="flex-1 py-4 bg-gray-50 text-gray-500 rounded-2xl text-[10px] font-black hover:bg-gray-100 transition-all uppercase tracking-widest">İPTAL</button>
              <button onClick={() => handleSil(silOnay)} className="flex-1 py-4 bg-gradient-to-r from-rose-500 to-red-600 text-white rounded-2xl text-[10px] font-black shadow-xl shadow-rose-200 transition-all uppercase tracking-widest active:scale-95">EVET, SİL</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes shake { 0%, 100% { transform: translateX(0); } 25% { transform: translateX(-5px); } 75% { transform: translateX(5px); } }
        .animate-shake { animation: shake 0.3s ease-in-out infinite; animation-iteration-count: 2; }
        .animate-bounce-subtle { animation: bounceSubtle 2s infinite; }
        @keyframes bounceSubtle { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
}
